import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Facebook, Twitter, Instagram, Linkedin, Mail, CheckCircle } from 'lucide-react';
import { useMedplum } from '@medplum/react';
import { trackContact } from './FacebookPixel';

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const medplum = useMedplum();
  const isAuthenticated = medplum.getActiveLogin() !== undefined;

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Track newsletter subscription as contact
    trackContact({ 
      content_category: 'newsletter',
      content_name: 'Newsletter Subscription'
    });
    console.log('Facebook Pixel: Contact event tracked (newsletter)');
    
    setIsSubscribed(true);
    setEmail('');
    setIsSubmitting(false);
  };

  return (
    <footer className="bg-gray-800 text-white pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div className="md:col-span-1">
            <div className="flex items-center mb-6">
              <Heart className="w-7 h-7 text-[#F4B6A1]" />
              <span className="ml-2 text-2xl font-bold">
                FeelHeard<span className="text-[#C2A4D6]">.me</span>
              </span>
            </div>
            <p className="text-gray-300 mb-6">
              Your AI reflection guide for empathetic listening and emotional support, available 24/7.
            </p>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-300 hover:text-[#F4B6A1] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-300 hover:text-[#F4B6A1] transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-300 hover:text-[#F4B6A1] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-300 hover:text-[#F4B6A1] transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                {isAuthenticated ? (
                  <Link to="/settings/subscription" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                    Pricing
                  </Link>
                ) : (
                  <a href="#pricing" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                    Pricing
                  </a>
                )}
              </li>
              <li>
                <a href="#testimonials" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                  Testimonials
                </a>
              </li>
              {/* FAQ link removed */}
              <li>
                <a 
                  href="#contact" 
                  className="text-gray-300 hover:text-[#B3D9E6] transition-colors"
                  onClick={() => {
                    trackContact({ content_category: 'footer_link' });
                    console.log('Facebook Pixel: Contact event tracked (footer link)');
                  }}
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/terms" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/data-protection" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                  Data Protection
                </Link>
              </li>
              <li>
                <Link to="/ai-ethics" className="text-gray-300 hover:text-[#B3D9E6] transition-colors">
                  AI Ethics Statement
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div id="contact">
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#F4B6A1]" />
                <a 
                  href="mailto:support@somatopia.com" 
                  className="text-gray-300 hover:text-[#B3D9E6] transition-colors"
                  onClick={() => {
                    trackContact({ content_category: 'email' });
                    console.log('Facebook Pixel: Contact event tracked (email click)');
                  }}
                >
                  support@somatopia.com
                </a>
              </li>
            </ul>
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-2">Subscribe to our newsletter</h4>
              {isSubscribed ? (
                <div className="flex items-center text-[#A9D0A1] gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Thank you for subscribing!</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="flex">
                  <input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="px-4 py-2 rounded-l-lg bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-[#C2A4D6] w-full"
                  />
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className={`bg-[#C2A4D6] hover:bg-[#B390C9] text-white px-4 py-2 rounded-r-lg transition-colors ${
                      isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              &copy; {new Date().getFullYear()} FeelHeard.me. All rights reserved.
            </p>
            <div className="text-gray-400 text-sm">
              <p>
                FeelHeard.me is not a replacement for professional mental health services. If you are in crisis, 
                please call your local emergency services or crisis helpline.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;